// Pure aggregation model for the Pipeline Line Report's chart + breakdown.
//
// Extracted from PipelineLineReport.tsx so the bucketing/count logic is unit
// testable (mirrors the fundedSummaryModel.ts pattern). Key guarantees:
//   - Pacific-pinned buckets + window (stable regardless of viewer timezone).
//   - A single in-range event list feeds BOTH the summary chips (rangeTotals)
//     and the per-bucket rows, so `sum(rows[status]) === rangeTotals[status].count`.
//   - Zero-count buckets are kept for chart continuity; the caller filters
//     empty rows out of the breakdown table only.
import { differenceInDays, parseISO, format } from 'date-fns';
import {
  PipelineLoan,
  PipelineStatus,
  getMilestoneDoneDate,
  getMilestoneDoneBy
} from '../types';
import {
  toLocalDateString,
  pacificMidnightAsUtcMs,
  addCalendarDays
} from '@/utils/dateHelpers';
import { getCanonicalEventDate, toPacificDateKey } from '../reporting';

export type Granularity = 'daily' | 'monthly';

export interface StatusLineConfig {
  status: PipelineStatus;
  label: string;
  color: string;
}

// Ordered pipeline stages shown as chart lines / breakdown columns. Moved here
// from PipelineLineReport so the model owns the canonical stage order.
export const STATUS_LINE_CONFIG: StatusLineConfig[] = [
  { status: 'started', label: 'Started', color: 'hsl(var(--muted-foreground))' },
  { status: 'loan_setup', label: 'Loan Setup', color: 'hsl(var(--chart-5))' },
  { status: 'processing', label: 'Processing', color: 'hsl(var(--chart-1))' },
  { status: 'submitted', label: 'Submitted', color: 'hsl(var(--chart-2))' },
  { status: 'cond_approval', label: 'Cond. Approval', color: 'hsl(var(--chart-3))' },
  { status: 'resubmitted', label: 'Resubmitted', color: 'hsl(var(--chart-4))' },
  { status: 'final_approval', label: 'Final Approval', color: 'hsl(var(--success))' },
  { status: 'docs_out', label: 'Docs Out', color: 'hsl(var(--warning))' },
  { status: 'funded', label: 'Funded', color: 'hsl(var(--primary))' },
  { status: 'purchased', label: 'Purchased', color: 'hsl(var(--chart-2))' }
];

// One expandable detail row (a single loan reaching a single milestone).
export interface LineDetail {
  status: PipelineStatus;
  label: string;
  color: string;
  loan: PipelineLoan;
  doneBy: string | null;
  doneDate: string;
}

// A chart point / breakdown row. `key` is the Pacific bucket key
// (YYYY-MM-DD daily, YYYY-MM monthly); each PipelineStatus holds its count.
export type LineRow = {
  key: string;
  label: string;
  total: number;
} & Record<PipelineStatus, number>;

export interface StatusTotal {
  count: number;
  amount: number;
}

export interface PipelineLineModel {
  granularity: Granularity;
  rangeTotals: Record<PipelineStatus, StatusTotal>;
  rows: LineRow[];
  detailsByKey: Map<string, LineDetail[]>;
}

interface LineEvent {
  status: PipelineStatus;
  label: string;
  color: string;
  loan: PipelineLoan;
  doneBy: string | null;
  doneDate: string;
  instant: Date;
  inRange: boolean;
  ptDay: string;
  ptMonth: string;
}

export interface TimeAxis {
  granularity: Granularity;
  keys: string[]; // ordered bucket keys: YYYY-MM-DD (daily) or YYYY-MM (monthly)
  labelFor: (key: string) => string;
  startMs: number; // inclusive Pacific window start (epoch ms)
  endMs: number; // inclusive Pacific window end (epoch ms)
}

/**
 * Build the Pacific-pinned time axis for a report range. Shared by the Line
 * Report (chart/breakdown buckets) and the Activity volume chart so both use
 * identical bucketing and window boundaries.
 *
 * Window boundaries use the picked *local* calendar day (toLocalDateString),
 * pinned to Pacific midnight — so a non-Pacific viewer's "June 1" pick stays
 * June 1, not the instant's PT day (May 31).
 */
export function buildTimeAxis(range: { from: Date; to: Date }): TimeAxis {
  const granularity: Granularity =
    differenceInDays(range.to, range.from) >= 90 ? 'monthly' : 'daily';

  const startKey = toLocalDateString(range.from);
  const endKey = toLocalDateString(range.to);
  const startMs = pacificMidnightAsUtcMs(startKey);
  const endMs = pacificMidnightAsUtcMs(addCalendarDays(endKey, 1)) - 1;

  const keys: string[] = [];
  const seen = new Set<string>();
  let cursor = startKey;
  while (cursor <= endKey) {
    const key = granularity === 'daily' ? cursor : cursor.slice(0, 7);
    if (!seen.has(key)) {
      seen.add(key);
      keys.push(key);
    }
    cursor = addCalendarDays(cursor, 1);
  }

  const labelFor = (key: string): string =>
    granularity === 'daily'
      ? format(parseISO(`${key}T12:00:00`), 'MMM d')
      : format(parseISO(`${key}-01T12:00:00`), 'MMM yyyy');

  return { granularity, keys, labelFor, startMs, endMs };
}

function emptyTotals(): Record<PipelineStatus, StatusTotal> {
  const totals = {} as Record<PipelineStatus, StatusTotal>;
  for (const cfg of STATUS_LINE_CONFIG) {
    totals[cfg.status] = { count: 0, amount: 0 };
  }
  return totals;
}

function makeRow(key: string, label: string): LineRow {
  const counts = Object.fromEntries(
    STATUS_LINE_CONFIG.map((c) => [c.status, 0])
  ) as Record<PipelineStatus, number>;
  return { key, label, total: 0, ...counts };
}

/**
 * Aggregate loans into the Line Report's chart/breakdown model.
 *
 * @param loans population already scoped by the parent's entity filters
 * @param range the selected date window (picker Dates; interpreted as the
 *   picked *calendar day* pinned to Pacific business days)
 */
export function buildPipelineLineModel(
  loans: PipelineLoan[],
  range: { from: Date; to: Date }
): PipelineLineModel {
  const { granularity, keys: orderedKeys, labelFor } = buildTimeAxis(range);
  const rangeStart = toLocalDateString(range.from);
  const rangeEnd = toLocalDateString(range.to);

  // Single event list: one entry per (status, loan) milestone completion.
  const events: LineEvent[] = [];
  for (const cfg of STATUS_LINE_CONFIG) {
    for (const loan of loans) {
      const milestoneDate = getMilestoneDoneDate(loan, cfg.status);
      const canonicalDate = cfg.status === 'funded'
        ? getCanonicalEventDate(loan, 'funded')
        : cfg.status === 'purchased'
          ? getCanonicalEventDate(loan, 'purchased')
          : null;
      const usesCanonicalDate = cfg.status === 'funded' || cfg.status === 'purchased';
      const doneDate = usesCanonicalDate ? canonicalDate : milestoneDate;
      const ptDay = canonicalDate || toPacificDateKey(milestoneDate);
      if (!doneDate || !ptDay) {
        continue;
      }
      const instant = parseISO(
        /^\d{4}-\d{2}-\d{2}$/.test(doneDate)
          ? `${doneDate}T12:00:00`
          : doneDate
      );
      if (isNaN(instant.getTime())) {
        continue;
      }
      events.push({
        status: cfg.status,
        label: cfg.label,
        color: cfg.color,
        loan,
        doneBy: getMilestoneDoneBy(loan, cfg.status),
        doneDate,
        instant,
        inRange: ptDay >= rangeStart && ptDay <= rangeEnd,
        ptDay,
        ptMonth: ptDay.slice(0, 7)
      });
    }
  }

  // Summary chips: exact in-range window, per status.
  const rangeTotals = emptyTotals();
  for (const ev of events) {
    if (!ev.inRange) {
      continue;
    }
    rangeTotals[ev.status].count += 1;
    rangeTotals[ev.status].amount += ev.loan.trans_details_loan_amt || 0;
  }

  const rowByKey = new Map<string, LineRow>();
  for (const key of orderedKeys) {
    rowByKey.set(key, makeRow(key, labelFor(key)));
  }

  const detailsByKey = new Map<string, LineDetail[]>();
  for (const ev of events) {
    if (!ev.inRange) {
      continue;
    }
    const key = granularity === 'daily' ? ev.ptDay : ev.ptMonth;
    const row = rowByKey.get(key);
    if (!row) {
      continue; // in-range event outside the axis — guard, should not happen
    }
    row[ev.status] += 1;
    row.total += 1;
    let list = detailsByKey.get(key);
    if (!list) {
      list = [];
      detailsByKey.set(key, list);
    }
    list.push({
      status: ev.status,
      label: ev.label,
      color: ev.color,
      loan: ev.loan,
      doneBy: ev.doneBy,
      doneDate: ev.doneDate
    });
  }

  // Detail rows sorted by stage order, then chronologically.
  const statusOrder = new Map(STATUS_LINE_CONFIG.map((c, i) => [c.status, i]));
  for (const list of detailsByKey.values()) {
    list.sort((a, b) => {
      const orderDiff =
        (statusOrder.get(a.status) ?? 0) - (statusOrder.get(b.status) ?? 0);
      if (orderDiff !== 0) {
        return orderDiff;
      }
      return a.doneDate.localeCompare(b.doneDate);
    });
  }

  const rows = orderedKeys.map((k) => rowByKey.get(k)!);
  return { granularity, rangeTotals, rows, detailsByKey };
}
